import * as React from 'react'

import invariant from 'invariant'
import {Navigate, Route, Routes, useLocation, useMatch, useNavigate} from 'react-router'

import {useAppSelector} from '../../hooks/redux'

import {goToCreatePricing, goToPricings} from '../../services/navigation'

import {Price, PriceFormula, PricingTab} from '../../types'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Tabs from '../../../../commons/src/components/tabs'
import Tab from '../../../../commons/src/components/tabs/Tab'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import ROUTES from '../../routes'
import PagePartial from '../page-partial'

import CreatePricingFormPartial from './create-pricing-form-partial'

const CreatePricingPage = () => {
  const provider = useAppSelector(state => state.core.provider)

  invariant(provider, 'Provider is missing.')

  const navigate = useNavigate()
  const routerLocation = useLocation()
  const locationState = routerLocation.state as undefined | {price: Price}
  const defaultPrice = locationState?.price

  const match = useMatch<'tab', string>({
    path: `${ROUTES.CREATE_PRICING}/:tab`,
  })
  const activeTab: PricingTab = (match?.params.tab as PricingTab) || PricingTab.THREE_D_PRINTING

  return (
    <PagePartial hideFooter>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToPricings(navigate, {activeTab})}
                />
                <Headline size="l" label="Create new pricing" className="u-no-margin-bottom" />
              </ActionBar>
            }
          >
            <Tabs
              activeTab={activeTab}
              onChange={tabKey => {
                goToCreatePricing(navigate, {
                  activeTab: tabKey as PricingTab,
                  price: defaultPrice,
                  replace: true,
                })
              }}
            >
              <Tab
                label="3D Printing"
                tabKey={PricingTab.THREE_D_PRINTING}
                disabled={defaultPrice && defaultPrice?.formula !== PriceFormula.THREE_D_PRINTING}
              />
              <Tab
                label="CNC Machining"
                tabKey={PricingTab.CNC_MACHINING}
                disabled={defaultPrice && defaultPrice?.formula !== PriceFormula.CNC_MACHINING}
              />
            </Tabs>
          </PageHeader>
        }
      >
        <Container size="l">
          <Routes>
            <Route
              path={PricingTab.THREE_D_PRINTING}
              element={
                <CreatePricingFormPartial
                  defaultPrice={defaultPrice}
                  provider={provider}
                  formula={PriceFormula.THREE_D_PRINTING}
                  onDone={() => {
                    goToPricings(navigate, {activeTab: PricingTab.THREE_D_PRINTING})
                  }}
                />
              }
            />
            <Route
              path={PricingTab.CNC_MACHINING}
              element={
                <CreatePricingFormPartial
                  defaultPrice={defaultPrice}
                  provider={provider}
                  formula={PriceFormula.CNC_MACHINING}
                  onDone={() => {
                    goToPricings(navigate, {activeTab: PricingTab.CNC_MACHINING})
                  }}
                />
              }
            />
            <Route path="*" element={<Navigate to={ROUTES.PRICINGS} />} />
          </Routes>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default CreatePricingPage
